import * as Sentry from "@sentry/react";

/**
 * Auth-flow failure reporter (same contract as Sandbox's
 * src/Services/logAuthError.js — copied, not shared, since the repos have no
 * common package).
 *
 * 5xx / network-level failures become Sentry events tagged with the auth step
 * and the backend's `x-request-id` (readable thanks to cy-sso's CORS
 * exposedHeaders) so one search in Sentry finds both the FE and BE event for
 * the same request. A lost refresh race and a missing local session are
 * normal too. 4xx responses (wrong OTP, unknown email, bad password) are
 * user events, not errors — they only leave a breadcrumb on the trail of
 * whatever is captured next.
 *
 * Never pass otp/password values inside `extra`.
 */
export const logAuthError = (step, err, extra = {}) => {
  try {
    const status = err?.response?.status;
    const requestId = err?.response?.headers?.["x-request-id"] || "none";
    const data = {
      authStep: step,
      status,
      requestId,
      responseMsg: err?.response?.data?.msg || err?.response?.data?.message,
      ...extra,
    };

    // Normal outcomes of a refresh, not faults: another tab won the refresh
    // race, or this page holds no session. They leave a breadcrumb only.
    const expected = err?.refreshRaceLost === true || err?.noLocalSession === true;

    if (expected || (status && status < 500)) {
      Sentry.addBreadcrumb({
        category: "auth",
        level: "info",
        message: `auth ${step} failed (${status ?? (err?.noLocalSession === true ? "no local session" : "refresh race lost")})`,
        data,
      });
      return;
    }

    Sentry.captureException(err, {
      tags: { flow: "auth", authStep: step, request_id: requestId },
      extra: data,
    });
  } catch (_) {
    // Reporting must never break the auth flow.
  }
};

export default logAuthError;
